import { StethoscopeImage } from "$components/stethoscope-image";
import type { CSSProperties, FC, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

const BRAND = "rgb(0, 79, 113)";
const MUTED = "#5c6b7a";

export interface ErrorStateProps {
  /**
   * page (default): full illustration + contact support, pushed down the viewport.
   * inline: compact, fills its host region (cards, tabs, table bodies, dropdowns).
   * boundary: like page, plus a debug box; used by ErrorBoundary.
   */
  variant?: "page" | "inline" | "boundary";
  title?: ReactNode;
  body?: ReactNode;
  /** In-app errors: its message is shown as a muted line. */
  error?: Error;
  /** Debug box (name/message/stack) for variant="boundary". */
  details?: { name: string; message: string; stack?: string };
  /** When set, the action button calls this; otherwise, if shown, it reloads. */
  onRetry?: () => void;
  retryLabel?: ReactNode;
  /** Show the reload button when there's no onRetry. Defaults on for boundary. */
  showRetry?: boolean;
  /** Page/boundary support line. Node to override the default, or false to hide. */
  contactSupport?: ReactNode | false;
  /** Override the illustration (defaults to the branded stethoscope). */
  illustration?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * The single error surface for the whole app. Uses raw HTML + inline styles and
 * accepts injectable translations, so it renders with or without MUI/intl contexts
 * (the root ErrorBoundary lives above the providers).
 */
export const ErrorState: FC<ErrorStateProps> = ({
  variant = "page",
  title = (
    <FormattedMessage
      id="errorPage.title"
      defaultMessage="Something went wrong"
    />
  ),
  body,
  error,
  details,
  onRetry,
  retryLabel = (
    <FormattedMessage id="errorPage.retryButton" defaultMessage="Try again" />
  ),
  showRetry,
  contactSupport,
  illustration,
  className,
  style,
}) => {
  const isInline = variant === "inline";
  const handleRetry = onRetry ?? (() => window.location.reload());
  const showButton =
    onRetry !== undefined || (showRetry ?? variant === "boundary");
  const showSupport = !isInline && contactSupport !== false;

  return (
    <div
      data-testid="error-page"
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: isInline ? "12px" : "24px",
        width: "100%",
        height: isInline ? "100%" : undefined,
        padding: isInline ? "24px" : "20vh 24px 24px",
        color: "#000",
        fontFamily: "sans-serif",
        ...style,
      }}
    >
      {illustration ?? (
        <StethoscopeImage
          style={{ width: isInline ? "72px" : "160px", height: "auto" }}
        />
      )}
      {isInline ? (
        <p style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>{title}</p>
      ) : (
        <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>
          {title}
        </h1>
      )}
      {body && (
        <p style={{ margin: 0, fontSize: "0.875rem", maxWidth: "320px" }}>
          {body}
        </p>
      )}
      {error?.message && (
        <p style={{ margin: 0, fontSize: "0.75rem", color: MUTED }}>
          {error.message}
        </p>
      )}
      {showButton && (
        <button
          type="button"
          onClick={handleRetry}
          style={{
            padding: isInline ? "8px 20px" : "12px 32px",
            borderRadius: "2rem",
            border: "none",
            backgroundColor: BRAND,
            color: "white",
            cursor: "pointer",
            fontSize: isInline ? "0.875rem" : "1rem",
          }}
        >
          {retryLabel}
        </button>
      )}
      {showSupport && (
        <p style={{ margin: 0, fontSize: "0.875rem", color: MUTED }}>
          {contactSupport ?? (
            <FormattedMessage
              id="errorPage.contactSupport"
              defaultMessage="If this persists, please report this to the Elephant team."
            />
          )}
        </p>
      )}
      {variant === "boundary" && details && (
        <div
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            maxWidth: "800px",
            color: "#FFF",
            border: "1px solid #00A0AF",
            backgroundColor: "#00B8C9",
            overflow: "auto",
          }}
        >
          <p style={{ fontWeight: 700, margin: 0 }}>{details.name}</p>
          <p style={{ fontSize: "0.8rem", margin: 0 }}>{details.message}</p>
          {details.stack && (
            <p style={{ fontSize: "0.8rem", color: "#EEE", margin: 0 }}>
              {details.stack}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
